import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { api, formatINR } from "@/lib/api";
import { CheckCircle2, Copy, Gift, Clock, Package } from "lucide-react";
import confetti from "canvas-confetti";
import toast from "react-hot-toast";
import BrandLogo from "@/components/BrandLogo";

export default function OrderSuccess() {
    const { orderId } = useParams();
    const [data, setData] = useState(null);
    const [revealed, setRevealed] = useState({});
    const nav = useNavigate();

    useEffect(() => {
        (async () => {
            try {
                const r = await api.get(`/orders/${orderId}`);
                setData(r.data);
                setTimeout(() => {
                    confetti({ particleCount: 140, spread: 80, origin: { y: 0.3 }, colors: ["#0B1437", "#0B1437", "#E4C86E"] });
                }, 350);
            } catch {
                nav("/orders");
            }
        })();
    }, [orderId, nav]);

    const copy = (code) => {
        navigator.clipboard.writeText(code);
        toast.success("Copied!");
    };

    if (!data) return <div className="py-20 text-center text-[#6C6F7A]">Loading your vouchers…</div>;

    return (
        <div className="max-w-3xl mx-auto px-4 md:px-8 py-10 md:py-14">
            <div className="text-center mb-8 rk-fade-up">
                <div className="w-16 h-16 rounded-full bg-[#1D5C42]/10 flex items-center justify-center mx-auto mb-4">
                    <CheckCircle2 className="w-9 h-9 text-[#1D5C42]" />
                </div>
                <h1 className="font-serif text-4xl md:text-5xl text-[#0B1437]">Order confirmed!</h1>
                <p className="text-[#6C6F7A] mt-2">
                    Order #{data.order.short_id} · {formatINR(data.order.amount_inr)}
                </p>
                {data.order.is_gift && (
                    <div className="inline-flex items-center gap-2 mt-4 bg-[#0B1437]/5 text-[#0B1437] px-4 py-2 rounded-full text-sm">
                        <Gift className="w-4 h-4" />
                        🎁 Sent to {data.order.gift?.recipient_name} via {data.order.gift?.delivery_channel}
                    </div>
                )}
            </div>

            <h2 className="font-serif text-2xl mb-4">Your Vouchers</h2>
            <div className="space-y-3">
                {data.vouchers.map((v) => (
                    <div
                        data-testid={`voucher-card-${v.id}`}
                        key={v.id}
                        className="bg-white border border-[#E5E0D5] rounded-2xl p-5 flex items-center gap-4"
                    >
                        <div className="w-16 h-16 rk-logo-box flex items-center justify-center shrink-0">
                            <BrandLogo brand={v} size="sm" />
                        </div>
                        <div className="flex-1 min-w-0">
                            <p className="text-[10px] uppercase tracking-wider text-[#6C6F7A]">{v.category}</p>
                            <p className="font-semibold">{v.brand_name}</p>
                            <p className="text-xs text-[#6C6F7A]">
                                Value {formatINR(v.denomination)} · Valid {v.validity || "1 Year"}
                            </p>
                        </div>
                        <div className="text-right">
                            {revealed[v.id] ? (
                                <div>
                                    <div className="font-mono text-sm font-bold text-[#0B1437]">{v.code}</div>
                                    <div className="text-[11px] text-[#6C6F7A]">PIN: {v.pin}</div>
                                    <button onClick={() => copy(`${v.code}  PIN: ${v.pin}`)} className="text-xs text-[#0B1437] inline-flex items-center gap-1 mt-1 hover:underline">
                                        <Copy className="w-3 h-3" /> Copy
                                    </button>
                                </div>
                            ) : (
                                <button
                                    data-testid={`reveal-${v.id}`}
                                    onClick={() => setRevealed({ ...revealed, [v.id]: true })}
                                    className="rk-btn-primary !py-2 !px-4 text-xs"
                                >
                                    Reveal Code
                                </button>
                            )}
                        </div>
                    </div>
                ))}
            </div>

            <div className="mt-8 bg-[#EFECE2] rounded-2xl p-5 text-sm">
                <div className="flex items-center gap-2 mb-2 font-semibold">
                    <Clock className="w-4 h-4 text-[#0B1437]" /> How to redeem
                </div>
                <p className="text-[#6C6F7A] leading-relaxed">
                    Use the voucher code at the brand's online store or retail outlet during checkout.
                    All voucher codes are also available under <Link to="/vouchers" className="text-[#0B1437] font-semibold">My Vouchers</Link>.
                </p>
            </div>

            <div className="flex gap-3 justify-center mt-6">
                <Link to="/catalog" className="rk-btn-ghost">Keep shopping</Link>
                <Link to="/vouchers" className="rk-btn-primary inline-flex items-center gap-2">
                    <Package className="w-4 h-4" /> View all vouchers
                </Link>
            </div>
        </div>
    );
}
